export type BillingFrequency = 'monthly' | 'annual' | 'one_off';

export function parseISODate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y!, m! - 1, d!, 12, 0, 0));
}

export function formatISODate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export function todayISO(): string {
  return formatISODate(new Date());
}

export function addBillingPeriod(
  fromISO: string,
  frequency: BillingFrequency,
): string | null {
  if (frequency === 'one_off') return null;
  const d = parseISODate(fromISO);
  if (frequency === 'monthly') d.setUTCMonth(d.getUTCMonth() + 1);
  else d.setUTCFullYear(d.getUTCFullYear() + 1);
  return formatISODate(d);
}

export function nextExpiryAfterRenew(opts: {
  previousExpiry: string | null;
  frequency: BillingFrequency;
  todayISO: string;
  explicitNext?: string | null;
}): string {
  if (opts.frequency === 'one_off') {
    if (!opts.explicitNext) throw new Error('one_off renew requires new_expiry_date');
    return opts.explicitNext;
  }
  let next = addBillingPeriod(opts.previousExpiry ?? opts.todayISO, opts.frequency)!;
  let guard = 0;
  while (next <= opts.todayISO && guard < 120) {
    next = addBillingPeriod(next, opts.frequency)!;
    guard += 1;
  }
  return next;
}

export function isComplianceDue(opts: {
  expiryDate: string | null;
  leadTimeDays: number;
  todayISO: string;
  active: boolean;
  deleted: boolean;
}): boolean {
  if (opts.deleted || !opts.active || !opts.expiryDate) return false;
  const exp = parseISODate(opts.expiryDate);
  exp.setUTCDate(exp.getUTCDate() - opts.leadTimeDays);
  return formatISODate(exp) <= opts.todayISO;
}

export function daysRemaining(expiryISO: string, today: string): number {
  const a = parseISODate(expiryISO).getTime();
  const b = parseISODate(today).getTime();
  return Math.round((a - b) / 86400000);
}

/** Banner dismissal lasts 7 days — long enough not to nag daily, short enough to return before most lead windows close. */
export const RENEWAL_BANNER_DISMISS_MS = 7 * 24 * 60 * 60 * 1000;

export const RENEWAL_BANNER_DISMISS_META = 'renewal_banner_dismissed_until';

export function isRenewalBannerDismissed(
  untilISO: string | undefined,
  nowMs = Date.now(),
): boolean {
  if (!untilISO) return false;
  const t = Date.parse(untilISO);
  return Number.isFinite(t) && t > nowMs;
}

export function dismissalExpiresAt(fromMs = Date.now()): string {
  return new Date(fromMs + RENEWAL_BANNER_DISMISS_MS).toISOString();
}

export function formatPence(pence: number | null | undefined): string {
  if (pence == null) return '—';
  const pounds = (pence / 100).toFixed(2);
  return `£${pounds}`;
}
